import { useEffect, useState } from "react";
import { usePlant } from "@/lib/context";
import api from "@/lib/api";
import { num, intf, inrFull, COLORS } from "@/lib/format";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Cell, ReferenceLine } from "recharts";

export default function Operations() {
  const { state } = usePlant();
  const [snaps, setSnaps] = useState([]);
  const [heats, setHeats] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [s, h] = await Promise.all([
          api.get("/plant/snapshots?hours=2"),
          api.get("/plant/heats?limit=40"),
        ]);
        if (alive) { setSnaps(s.data.snapshots || []); setHeats(h.data.heats || []); }
      } catch {}
    };
    load();
    const id = setInterval(load, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!state) return <div className="p-10 text-[#8F93A2]">Loading…</div>;

  const holdingActive = state.furnaces?.some((f) => f.holding_active);
  const maxHoldMin = Math.max(...(state.furnaces?.map((f) => f.holding_duration_min) || [0]));
  const tariff = state.plant_params?.tariff_per_kwh || 6.5;
  const holdingKw = 0.35 * 550 * 2; // both furnaces holding at 192kW each
  const holdingCostNow = (state.shift?.holding_minutes_total || 0) / 60.0 * holdingKw * tariff;

  // DISA sync data (Fix 3.2) — plot moulds/hr and equivalent furnace mould demand,
  // not raw counts. Furnace_mould_rate = (60 / T2T) × Charge_kg / Avg_mould_metal_kg
  const avgMouldKg = 8.5;  // kg of metal per mould
  const t2tAvg = state.shift?.avg_t2t_min || 36.0;
  const furnaceMouldEquivalent = (60.0 / t2tAvg) * 500.0 / avgMouldKg * 2; // × 2 for both furnaces
  const syncData = snaps.map((s) => {
    const t2t = s.metrics?.ce_07 ? 36 : 36;  // fallback
    return {
      t: new Date(s.plant_time).toLocaleTimeString("en-IN", { hour12: false, hour: "2-digit", minute: "2-digit" }),
      mouldRate: s.tags?.DISA_MOULD_RATE || 0,
      furnaceEquiv: furnaceMouldEquivalent,
    };
  });

  // Per-furnace stats
  const fStats = (fid) => {
    const fh = heats.filter((h) => h.furnace_id === fid).slice(0, 12);
    if (!fh.length) return { avg: 0, sd: 0, best: 0, worst: 0, above: 0, below: 0 };
    const t2ts = fh.map((h) => h.tap_to_tap_min);
    const avg = t2ts.reduce((a, b) => a + b, 0) / t2ts.length;
    const sd = Math.sqrt(t2ts.reduce((a, b) => a + (b - avg) ** 2, 0) / t2ts.length);
    return {
      avg, sd, best: Math.min(...t2ts), worst: Math.max(...t2ts),
      above: t2ts.filter((x) => x > 28).length, below: t2ts.filter((x) => x <= 28).length,
    };
  };

  const f1 = fStats(1);
  const f2 = fStats(2);

  const partialHeats = heats.filter((h) => h.charge_total_kg < 425).slice(0, 5);

  return (
    <div className="space-y-5">
      <div>
        <div className="label-small">Operations Intelligence</div>
        <h1 className="font-display text-3xl uppercase tracking-tight font-bold">Cost of Poor Coordination</h1>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-8 panel">
          <div className="panel-header">
            <span>DISAMATIC Sync Monitor</span>
            <span className={`badge-status ${holdingActive ? "red" : state.tags?.DISA_MOULD_RATE < 80 ? "yellow" : "green"}`}>
              {holdingActive ? "HOLDING" : state.tags?.DISA_MOULD_RATE < 80 ? "AT RISK" : "IN SYNC"}
            </span>
          </div>
          <div className="p-3 h-[260px]" data-testid="disa-sync-chart">
            <ResponsiveContainer>
              <LineChart data={syncData}>
                <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                <XAxis dataKey="t" stroke={COLORS.textDim} fontSize={10} />
                <YAxis stroke={COLORS.textDim} fontSize={10} domain={[0, 450]} label={{ value: "moulds/hr", angle: -90, fill: COLORS.textDim, fontSize: 10, position: "insideLeft" }} />
                <Tooltip contentStyle={{ background: "#0C0D12", border: "1px solid #1F212A" }} />
                <ReferenceLine y={furnaceMouldEquivalent} stroke={COLORS.amber} strokeDasharray="3 3" label={{ value: `Furnace demand ${num(furnaceMouldEquivalent, 0)} m/hr`, fill: COLORS.amber, fontSize: 10, position: "insideTopRight" }} />
                <Line type="monotone" dataKey="mouldRate" stroke={COLORS.blue} strokeWidth={2} dot={false} isAnimationActive={false} name="DISA mph" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={`col-span-12 md:col-span-4 panel p-5 ${holdingActive ? "border-2 border-[#FF3333]" : ""}`} data-testid="holding-ticker">
          <div className="label-small">Live Holding Event Cost</div>
          <div className="metric-large mt-2" style={{ color: holdingActive ? COLORS.red : COLORS.text }}>
            {inrFull(holdingCostNow)}
          </div>
          <div className="text-xs text-[#8F93A2] mt-1">
            Total this shift · {num(state.shift?.holding_minutes_total, 1)} min holding
          </div>
          {holdingActive && (
            <div className="mt-3 p-3 bg-[#FF3333]/10 border border-[#FF3333]/30">
              <div className="text-[#FF3333] font-display uppercase tracking-wider text-sm font-bold">⚡ ACTIVE EVENT</div>
              <div className="text-xs text-white/90 mt-1">
                Furnace held {num(maxHoldMin, 1)} min so far. Burning ~{inrFull(holdingKw * tariff)}/hour.
              </div>
            </div>
          )}
        </div>

        <div className="col-span-12 md:col-span-6 panel">
          <div className="panel-header"><span>Shift Discipline — F1</span></div>
          <ShiftScorecard stats={f1} testid="discipline-f1" />
        </div>

        <div className="col-span-12 md:col-span-6 panel">
          <div className="panel-header"><span>Shift Discipline — F2</span></div>
          <ShiftScorecard stats={f2} testid="discipline-f2" />
        </div>

        <div className="col-span-12 md:col-span-6 panel">
          <div className="panel-header"><span>Partial Heat Detection</span><span className="text-[10px] text-[#8F93A2]">&lt;85% of capacity</span></div>
          <div className="p-4" data-testid="partial-heats">
            {partialHeats.length ? partialHeats.map((h, idx) => (
              <div key={h.id || `partial-${h.furnace_id}-${h.heat_number}-${idx}`} className="flex items-center justify-between py-2 border-b border-[#14151C] text-sm">
                <span>Heat #{h.heat_number} (F{h.furnace_id})</span>
                <span className="font-mono">{intf(h.charge_total_kg)} kg</span>
                <span className="text-[#FF3333] font-mono text-xs">
                  +{num((550 - h.charge_total_kg/1000*1100), 0)} kWh/t penalty
                </span>
              </div>
            )) : <div className="text-sm text-[#8F93A2] text-center py-4">No partial heats in current window</div>}
          </div>
        </div>

        <div className="col-span-12 md:col-span-6 panel">
          <div className="panel-header"><span>Monthly Coordination Loss</span></div>
          <div className="p-4 space-y-2" data-testid="coord-loss">
            <CoordLossRow label="Holding events" value={state.shift.holding_minutes_total / 60 * holdingKw * tariff * 26 * 2} />
            <CoordLossRow label="Shift changeover idle" value={45 * holdingKw * tariff / 60 * 26 * 2} />
            <CoordLossRow label="Partial heats" value={partialHeats.length * 120 * tariff * 26 * 2} />
            <div className="border-t border-[#1F212A] mt-2 pt-2 flex items-center justify-between">
              <span className="label-small">YTD total</span>
              <span className="metric-mid text-[#FF3333]">
                {inrFull((state.shift.holding_minutes_total / 60 * holdingKw * tariff * 26 * 2 + 45 * holdingKw * tariff / 60 * 26 * 2 + partialHeats.length * 120 * tariff * 26 * 2) * 6)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ShiftScorecard({ stats, testid }) {
  return (
    <div className="p-4 grid grid-cols-3 gap-3 text-center" data-testid={testid}>
      <div><div className="label-small">Avg</div><div className="metric-mid">{num(stats.avg, 1)}</div></div>
      <div><div className="label-small">σ</div><div className="metric-mid">{num(stats.sd, 1)}</div></div>
      <div><div className="label-small">Best</div><div className="metric-mid text-[#00FF66]">{num(stats.best, 1)}</div></div>
      <div><div className="label-small">Worst</div><div className="metric-mid text-[#FF3333]">{num(stats.worst, 1)}</div></div>
      <div><div className="label-small">Above target</div><div className="metric-mid text-[#FF3333]">{stats.above}</div></div>
      <div><div className="label-small">Below target</div><div className="metric-mid text-[#00FF66]">{stats.below}</div></div>
    </div>
  );
}

function CoordLossRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-[#8F93A2]">{label}</span>
      <span className="font-mono text-[#FF3333]">{inrFull(value)}</span>
    </div>
  );
}
